import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import styled, { css, useTheme } from 'styled-components';
import { ICreatorFashionContentsDB, IRecommandCreatorDB } from 'type/wconcepdb';

import TableCell from 'components/common/table/cell/TableCell';
import {
	useGetRecommendationCreatorList,
	useGetRecommendationList,
} from 'hooks/queries/creator';
import useRecommendTable from 'hooks/table/useRecommendTable';
import useGetSearchParams from 'hooks/useGetSearchParams';
import CoreLoading from 'shared/CoreLoading';
import CoreSvg from 'shared/CoreSvg';
import Icon from 'shared/icons';
import style from 'styles/styled-components/style';
import { recommend_header_arr } from 'utils/table';

import IconDropdown from './IconDropdown';
import SearchContentBoard from './SearchContentBoard';
import SearchFashionContent from './SearchFashionContent';

const convertRatingToKorean = (rating: string): string => {
	switch (rating) {
		case 's':
			return 'S등급/메가';
		case 'a':
			return 'A등급/매크로';
		case 'b':
			return 'B등급/마이크로';
		default:
			return 'S등급/메가';
	}
};

const convertRatingToLevel = (rating: string, platform: 'ig' | 'yt'): string => {
	const fllowerKr = platform === 'ig' ? '팔로워' : '구독자';
	const megaKr = platform === 'ig' ? '30만 이상' : '20만 이상';
	const macroKr = platform === 'ig' ? '5~30만' : '5만 이상';
	const microKr = platform === 'ig' ? '5만 이하' : '1만 이상';
	switch (rating) {
		case 's':
			return `메가(${fllowerKr} ${megaKr})`;
		case 'a':
			return `매크로(${fllowerKr} ${macroKr})`;
		case 'b':
			return `마이크로(${fllowerKr} ${microKr})`;
		default:
			return `메가(${fllowerKr} ${megaKr})`;
	}
};

const RecommendList = () => {
	const [search_params, setSearchParams] = useSearchParams();
	const theme = useTheme();
	const navigate = useNavigate();
	const [platform, recommendationId, rating, keyword] = useGetSearchParams([
		'platform',
		'recommendationId',
		'rating',
		'keyword',
	]);

	const { data: recommandGroupList, isLoading: recommandGroupListLoading } =
		useGetRecommendationList();

	const paramPlatform = platform === 'ig' ? 'instagram' : 'youtube';
	const { data: recommandData, isLoading: recommandDataLoading } =
		useGetRecommendationCreatorList(recommendationId, paramPlatform);
	// recommendData를 추천 크리에이터 리스트로 사용 – 배열이 아닐 경우 빈 배열 사용

	let recommandCreatorList: IRecommandCreatorDB[] = [];
	if (rating === 's') recommandCreatorList = recommandData?.mega ?? [];
	if (rating === 'a') recommandCreatorList = recommandData?.macro ?? [];
	if (rating === 'b') recommandCreatorList = recommandData?.micro ?? [];

	// keyword가 있을 경우 필터링, 없을 경우 통과
	const filteredCreatorList = keyword
		? recommandCreatorList.filter(
				(creator) =>
					creator.username.toLowerCase().includes(keyword.toLowerCase()) ||
					creator.biography.toLowerCase().includes(keyword.toLowerCase()),
		  )
		: recommandCreatorList;

	const [activePk, setActivePk] = useState<number | null>(null);

	const match_cell_mapper = useRecommendTable(platform as 'ig' | 'yt');

	const handleRowClick = (row_data: IRecommandCreatorDB) => {
		const creatorPk = row_data.creatorPk;
		navigate(`/creator/report/${platform}/${recommendationId}/${creatorPk}`);
	};

	const groups = recommandGroupList;
	const month = groups?.find(
		(group) => group.recommendationId === parseInt(recommendationId || ''),
	)?.recommendationMonth;
	const formattedMonth = month
		? `${new Date(month).getFullYear()}년 ${
				new Date(month).getMonth() + 1
		  }월 크리에이터 추천`
		: '';

	const brandRating = `${rating?.toUpperCase()}등급`;
	const createrLevel = `${convertRatingToLevel(rating, platform as 'ig' | 'yt')}`;
	useEffect(() => {
		setActivePk(null);
	}, [platform]);

	///로딩중
	if (recommandGroupListLoading || recommandDataLoading) {
		return (
			<List>
				<Grid _height="calc(100vh - 400px)" flex="center">
					<CoreLoading color_type="black" />
				</Grid>
			</List>
		);
	}

	return (
		<List>
			<HeaderBox flex="start" direction="row" _gap="16px" _margin="0 0 16px 0">
				<Grid _width="auto" flex="start" direction="row" _gap="8px">
					<Text typo="p_bold">{formattedMonth}</Text>
					<Contour _height="21px" _width="1px" />
				</Grid>

				<Grid _width="auto" flex="start" direction="row" _gap="8px">
					<Text typo="p" color={theme.colors.gray.gray80_text}>
						{'입점 브랜드 등급: '}
					</Text>
					<Text typo="p_bold" color={theme.colors.gray.gray80_text}>
						{brandRating}
					</Text>
				</Grid>
				<Contour _height="40%" _width="1px" />
				<Grid _width="auto" flex="start" direction="row" _gap="8px">
					<Text typo="p" color={theme.colors.gray.gray80_text}>
						{'매칭 크리에이터 레벨: '}
					</Text>
					<Text typo="p_bold" color={theme.colors.gray.gray80_text}>
						{createrLevel}
					</Text>
				</Grid>
			</HeaderBox>

			<TableHeaderWrapper>
				{recommend_header_arr.map((item, idx) => (
					<React.Fragment key={idx}>
						<TableCell cell_type="common" _width={item.width}>
							<Grid
								_width={`${item.width}px`}
								flex="center"
								align="center"
								onClick={() => {
									if (item.name === 'contents' || item.name === 'link') return;
									search_params.set('sort', item.name);
									setSearchParams(search_params, { replace: true });
								}}
							>
								<Text white_space="pre" typo="span">
									{item.header}
								</Text>
							</Grid>
						</TableCell>
						{/* 구분 선 */}
						{idx !== recommend_header_arr?.length - 1 && (
							<Contour _height="21px" _width="1px" />
						)}
					</React.Fragment>
				))}
			</TableHeaderWrapper>
			<TableResultWrapper>
				{filteredCreatorList?.map((data, row_idx: number) => (
					<React.Fragment key={row_idx}>
						<TableRow selected={false}>
							{recommend_header_arr.map((item, idx) => {
								return (
									<React.Fragment key={idx}>
										{match_cell_mapper[item.name](data, idx)}
										{item.name === 'fashion_contents' && (
											<TableCell
												_width={recommend_header_arr[idx].width}
												cell_type="center"
											>
												<IconDropdown>
													<IconDropdown.Trigger>
														<CoreSvg>
															<Icon.Picture />
														</CoreSvg>
													</IconDropdown.Trigger>
													<IconDropdown.View>
														<SearchFashionContent
															dataSources={data.fashionContents}
															platform={platform as 'ig' | 'yt'}
														/>
													</IconDropdown.View>
												</IconDropdown>
											</TableCell>
										)}
										{item.name === 'collaboration' && (
											<TableCell
												_width={recommend_header_arr[idx].width}
												cell_type="center"
											>
												<Grid
													flex="center"
													_width="100%"
													_height="100%"
													onClick={(e) => {
														e.stopPropagation();
														if (activePk === data.creatorPk) {
															setActivePk(null);
														} else setActivePk(data.creatorPk);
													}}
												>
													{data.collaboratedContents?.length > 0 ? (
														activePk === data.creatorPk ? (
															<CoreSvg>
																<Icon.DropArrowUp />
															</CoreSvg>
														) : (
															<CoreSvg>
																<Icon.DropArrowDown />
															</CoreSvg>
														)
													) : (
														<CoreSvg _size="14px" _fill={theme.colors.gray.gray15}>
															<Icon.Close />
														</CoreSvg>
													)}
												</Grid>
											</TableCell>
										)}
									</React.Fragment>
								);
							})}
						</TableRow>
						{activePk === data.creatorPk && (
							<SearchContentBoard values={data.collaboratedContents} />
						)}
					</React.Fragment>
				))}
			</TableResultWrapper>
		</List>
	);
};
const { Grid, Text } = style;

const TableHeaderWrapper = styled(Grid)`
	height: 66px;
	background-color: ${({ theme }) => theme.colors.gray.gray_white};
	border-bottom: 1px solid ${({ theme }) => theme.colors.gray.gray15};
	border-top-left-radius: 8px;
	border-top-right-radius: 8px;
	padding: 0 16px;

	${({ theme }) => theme.common.flexStart};

	div {
		cursor: pointer;
	}
`;

const TableResultWrapper = styled(Grid)`
	margin-bottom: 8px;
`;

const Contour = styled(Grid)`
	background-color: ${({ theme }) => theme.colors.gray.gray15};
	min-width: 1px;
`;
const TableRow = styled(Grid)<{ selected: boolean; onClick?: () => void }>`
	min-width: calc(100% - 32px);
	height: 65px;

	border-radius: 8px;
	margin-top: 8px;
	padding: 0 16px;
	/* cursor: pointer; */

	${({ theme }) => theme.common.flexStart}

	background-color: ${({ theme }) => theme.colors.gray.gray_white};
	border: 1px solid ${({ theme }) => theme.colors.gray.gray_white};

	${({ theme, selected }) =>
		selected &&
		css`
			border: 1px solid ${theme.colors.gray.gray_black};
		`}

	&:hover {
		cursor: ${({ onClick }) => (onClick ? 'pointer' : 'default')};
		border-color: ${({ theme }) => theme.colors.uplus.bg10_lighten};
		background-color: ${({ theme }) => theme.colors.uplus.bg10_lighten};
		${({ theme, selected }) =>
			selected &&
			css`
				border-color: ${theme.colors.uplus.primary50_key};
			`}
	}
`;
const List = styled.div`
	width: 100%;
	height: max-content;
	max-width: 1400px;

	${({ theme }) => theme.common.flexStart};
	flex-direction: column;

	gap: 4px;
`;

const StateText = styled(Text)`
	${({ theme }) => theme.typo.body.span};
`;

const HeaderBox = styled(Grid)`
	min-width: calc(100% - 32px);
	height: 49px;

	border-radius: 8px;
	margin-top: 8px;
	padding: 0 16px;

	background-color: ${({ theme }) => theme.colors.gray.gray05_bg};
	border: 1px solid ${({ theme }) => theme.colors.gray.gray15};
`;

export default RecommendList;

const mockupFashionData: ICreatorFashionContentsDB[] = [
	{
		contentImage: '',
		description:
			'체크 셔츠에 폴로 셔츠를 겹쳐 입고 커다란 안경을 쓰고 슬리퍼를 끌고 런웨이에 나왔습니다. 너드는 주류가 아니었습니다. 확실히 아니었죠. 그때를 떠올리면, 여기에는 옮겨 적을 수 없는 나쁜 단어들이 떠오르니까요. 너 나 할 것 없이 안경을 벗고 렌즈를 끼던 시대에서 안경을 쓰는 것이 멋스러운 세상이 됐어요!',
		likeCount: 100,
		publishedAt: '2023-10-01T12:00:00Z',
	},
	{
		contentImage: '',
		description:
			'체크 셔츠에 폴로 셔츠를 겹쳐 입고 커다란 안경을 쓰고 슬리퍼를 끌고 런웨이에 나왔습니다. 너드는 주류가 아니었습니다. 확실히 아니었죠. 그때를 떠올리면, 여기에는 옮겨 적을 수 없는 나쁜 단어들이 떠오르니까요. 너 나 할 것 없이 안경을 벗고 렌즈를 끼던 시대에서 안경을 쓰는 것이 멋스러운 세상이 됐어요!',
		likeCount: 100,
		publishedAt: '2023-10-02T12:00:00Z',
	},
	{
		contentImage: '',
		description:
			'체크 셔츠에 폴로 셔츠를 겹쳐 입고 커다란 안경을 쓰고 슬리퍼를 끌고 런웨이에 나왔습니다. 너드는 주류가 아니었습니다. 확실히 아니었죠. 그때를 떠올리면, 여기에는 옮겨 적을 수 없는 나쁜 단어들이 떠오르니까요. 너 나 할 것 없이 안경을 벗고 렌즈를 끼던 시대에서 안경을 쓰는 것이 멋스러운 세상이 됐어요!',
		likeCount: 100,
		publishedAt: '2023-10-03T12:00:00Z',
	},
	{
		contentImage: '',
		description:
			'체크 셔츠에 폴로 셔츠를 겹쳐 입고 커다란 안경을 쓰고 슬리퍼를 끌고 런웨이에 나왔습니다. 너드는 주류가 아니었습니다. 확실히 아니었죠. 그때를 떠올리면, 여기에는 옮겨 적을 수 없는 나쁜 단어들이 떠오르니까요. 너 나 할 것 없이 안경을 벗고 렌즈를 끼던 시대에서 안경을 쓰는 것이 멋스러운 세상이 됐어요!',
		likeCount: 100,
		publishedAt: '2023-10-03T12:00:00Z',
	},
];
